import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { InvestmentsReadonlyTableComponent } from '@app/components/investments-readonly-table/investments-readonly-table.component';
import type { Investment } from '@app/models/investment.models';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ToastService } from '@app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-investment-list',
  standalone: true,
  imports: [CommonModule, InvestmentsReadonlyTableComponent],
  templateUrl: './investment-list.component.html',
  styleUrl: './investment-list.component.scss',
})
export class InvestmentListComponent {
  protected readonly isLoading = signal(true);
  protected readonly investments = signal<Investment[]>([]);

  private readonly investmentService = inject(ProjectAdminService);
  private readonly translateService = inject(TranslateService);
  private readonly toast = inject(ToastService);

  ngOnInit(): void {
    this.loadInvestments();
  }

  protected acceptInvestment(investmentId: string): void {
    this.investmentService.acceptInvestment(investmentId).subscribe({
      next: () => {
        this.toast.success(
          this.translateService.instant(
            'admin.allProjects.toasts.investmentConfirmed',
          ),
        );
        this.loadInvestments();
      },
      error: () => {
        this.toast.error(
          this.translateService.instant(
            'admin.allProjects.toasts.investmentConfirmFailed',
          ),
        );
      },
    });
  }

  protected rejectInvestment(investmentId: string): void {
    this.investmentService.refuseInvestment(investmentId).subscribe({
      next: () => {
        this.toast.success(
          this.translateService.instant(
            'admin.allProjects.toasts.investmentRejected',
          ),
        );
        this.loadInvestments();
      },
      error: () => {
        this.toast.error(
          this.translateService.instant(
            'admin.allProjects.toasts.investmentRejectFailed',
          ),
        );
      },
    });
  }

  protected approveInvestment(investmentId: string): void {
    this.investmentService.markInvestmentAsCredited(investmentId).subscribe({
      next: () => {
        this.toast.success(
          this.translateService.instant(
            'admin.allProjects.toasts.investmentApproved',
          ),
        );
        this.loadInvestments();
      },
      error: () => {
        this.toast.error(
          this.translateService.instant(
            'admin.allProjects.toasts.investmentApproveFailed',
          ),
        );
      },
    });
  }

  private loadInvestments(): void {
    this.isLoading.set(true);
    this.investmentService
      .getInvestments({
        skip: 0,
        take: 10000,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          const mapped: Investment[] = (response?.data ?? []).map((item) => ({
            ...item,
            accountOwner: null,
            accountNumber: null,
          }));
          this.investments.set(mapped);
        },
        error: () => {
          this.investments.set([]);
          this.toast.error(
            this.translateService.instant('admin.toasts.tableLoadFailed'),
          );
        },
      });
  }
}
